/**
 * 原生JavaScript DOM滚动工具函数
 * 支持多种滚动方式和配置选项
 */

export interface ScrollOptions {
  /** 滚动行为：smooth(平滑) | instant(瞬间) | auto(自动) */
  behavior?: ScrollBehavior;
  /** 滚动到元素顶部、中心还是底部 */
  block?: 'start' | 'center' | 'end' | 'nearest';
  /** 水平滚动位置 */
  inline?: 'start' | 'center' | 'end' | 'nearest';
  /** 偏移量，正数向下偏移，负数向上偏移 */
  offset?: number;
  /** 动画持续时间(ms)，仅在behavior为smooth时有效 */
  duration?: number;
  /** 滚动容器选择器，默认为window */
  container?: string | HTMLElement;
  /** 是否优先使用原生scrollIntoView方法，默认为true */
  useScrollIntoView?: boolean;
}

/**
 * 滚动到指定DOM元素
 * @param element 目标元素或选择器
 * @param options 滚动配置选项
 */
export function scrollToElement(
  element: string | HTMLElement | null,
  options: ScrollOptions = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      // 获取目标元素
      const targetElement = typeof element === 'string' 
        ? document.querySelector(element) 
        : element;

      if (!targetElement) {
        reject(new Error('目标元素未找到'));
        return;
      }

      const {
        behavior = 'smooth',
        block = 'start',
        inline = 'nearest',
        offset = 0,
        duration = 300,
        container,
        useScrollIntoView = true
      } = options;

      // 优先使用原生 scrollIntoView 方法
      if (useScrollIntoView) {
        try {
          // 支持 offset，通过 targetElement 找到滚动容器，然后调整
          if (offset === 0) {
            targetElement.scrollIntoView({
              behavior,
              block,
              inline
            });
          } else {
            // 查找最近的可滚动容器
            let scrollParent: HTMLElement | Window = window;
            let node: HTMLElement | null = targetElement.parentElement;
            while (node && node !== document.body) {
              const overflowY = window.getComputedStyle(node).overflowY;
              if (overflowY === 'auto' || overflowY === 'scroll') {
                scrollParent = node;
                break;
              }
              node = node.parentElement;
            }
            // 获取目标元素和容器的位置信息
            const targetRect = targetElement.getBoundingClientRect();
            const containerRect = scrollParent === window
              ? { top: 0, left: 0 }
              : (scrollParent as HTMLElement).getBoundingClientRect();

            let scrollTop: number;
            if (scrollParent === window) {
              scrollTop = window.pageYOffset + targetRect.top - containerRect.top + offset;
              window.scrollTo({
                top: scrollTop,
                behavior
              });
            } else {
              const containerEl = scrollParent as HTMLElement;
              scrollTop = containerEl.scrollTop + targetRect.top - containerRect.top + offset;
              containerEl.scrollTo({
                top: scrollTop,
                behavior
              });
            }
          }
          resolve();
          return;
        } catch (error) {
          // 如果 scrollIntoView 失败，继续使用自定义实现
          console.warn('scrollIntoView 失败，使用自定义滚动实现:', error);
        }
      }

      // 获取滚动容器
      const scrollContainer = container 
        ? (typeof container === 'string' 
            ? document.querySelector(container) 
            : container)
        : window;

      if (!scrollContainer) {
        reject(new Error('滚动容器未找到'));
        return;
      }

      // 计算目标位置
      const targetRect = targetElement.getBoundingClientRect();
      const containerRect = scrollContainer === window 
        ? { top: 0, left: 0, height: window.innerHeight, width: window.innerWidth }
        : (scrollContainer as HTMLElement).getBoundingClientRect();

      let scrollTop = 0;
      let scrollLeft = 0;

      if (scrollContainer === window) {
        // 窗口滚动
        scrollTop = window.pageYOffset + targetRect.top - containerRect.top + offset;
        scrollLeft = window.pageXOffset + targetRect.left - containerRect.left;
      } else {
        // 容器滚动
        const containerElement = scrollContainer as HTMLElement;
        scrollTop = containerElement.scrollTop + targetRect.top - containerRect.top + offset;
        scrollLeft = containerElement.scrollLeft + targetRect.left - containerRect.left;
      }

      // 根据block参数调整垂直位置
      if (block === 'center') {
        const containerHeight = scrollContainer === window 
          ? window.innerHeight 
          : (scrollContainer as HTMLElement).clientHeight;
        scrollTop -= containerHeight / 2 - targetRect.height / 2;
      } else if (block === 'end') {
        const containerHeight = scrollContainer === window 
          ? window.innerHeight 
          : (scrollContainer as HTMLElement).clientHeight;
        scrollTop -= containerHeight - targetRect.height;
      }

      // 根据inline参数调整水平位置
      if (inline === 'center') {
        const containerWidth = scrollContainer === window 
          ? window.innerWidth 
          : (scrollContainer as HTMLElement).clientWidth;
        scrollLeft -= containerWidth / 2 - targetRect.width / 2;
      } else if (inline === 'end') {
        const containerWidth = scrollContainer === window 
          ? window.innerWidth 
          : (scrollContainer as HTMLElement).clientWidth;
        scrollLeft -= containerWidth - targetRect.width;
      }

      // 执行滚动
      if (behavior === 'smooth' && duration > 0) {
        smoothScrollTo(scrollContainer as Window | HTMLElement, scrollLeft, scrollTop, duration).then(resolve);
      } else {
        if (scrollContainer === window) {
          window.scrollTo({
            left: scrollLeft,
            top: scrollTop,
            behavior: behavior as ScrollBehavior
          });
        } else {
          (scrollContainer as HTMLElement).scrollTo({
            left: scrollLeft,
            top: scrollTop,
            behavior: behavior as ScrollBehavior
          });
        }
        resolve();
      }
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * 平滑滚动动画
 * @param container 滚动容器
 * @param x 目标x坐标
 * @param y 目标y坐标
 * @param duration 动画持续时间
 */
function smoothScrollTo(
  container: Window | HTMLElement,
  x: number,
  y: number,
  duration: number
): Promise<void> {
  return new Promise((resolve) => {
    const startTime = performance.now();
    
    const getCurrentScroll = () => {
      if (container === window) {
        return { x: window.pageXOffset, y: window.pageYOffset };
      } else {
        const el = container as HTMLElement;
        return { x: el.scrollLeft, y: el.scrollTop };
      }
    };

    const setScroll = (x: number, y: number) => {
      if (container === window) {
        window.scrollTo(x, y);
      } else {
        const el = container as HTMLElement;
        el.scrollLeft = x;
        el.scrollTop = y;
      }
    };

    const startScroll = getCurrentScroll();
    const deltaX = x - startScroll.x;
    const deltaY = y - startScroll.y;

    function animate(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // 使用easeInOutCubic缓动函数
      const easeProgress = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      const currentX = startScroll.x + deltaX * easeProgress;
      const currentY = startScroll.y + deltaY * easeProgress;

      setScroll(currentX, currentY);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        resolve();
      }
    }

    requestAnimationFrame(animate);
  });
}

/**
 * 检查元素是否在可视区域内
 * @param element 目标元素
 * @param container 滚动容器，默认为window
 * @param threshold 阈值，0-1之间，表示元素需要多少比例可见才算在视口内
 */
export function isElementInViewport(
  element: string | HTMLElement | null,
  container: string | HTMLElement | Window = window,
  threshold: number = 0
): boolean {
  const targetElement = typeof element === 'string' 
    ? document.querySelector(element) 
    : element;

  if (!targetElement) return false;

  const elementRect = targetElement.getBoundingClientRect();
  const containerRect = container === window 
    ? { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth }
    : (container as HTMLElement).getBoundingClientRect();

  const visibleHeight = Math.min(elementRect.bottom, containerRect.bottom) - 
                       Math.max(elementRect.top, containerRect.top);
  const visibleWidth = Math.min(elementRect.right, containerRect.right) - 
                      Math.max(elementRect.left, containerRect.left);

  const elementArea = elementRect.height * elementRect.width;
  const visibleArea = Math.max(0, visibleHeight) * Math.max(0, visibleWidth);
  const visibilityRatio = elementArea > 0 ? visibleArea / elementArea : 0;

  return visibilityRatio >= threshold;
}

/**
 * 滚动到第一个错误元素（适用于表单验证）
 * @param formContainer 表单容器选择器
 * @param errorSelector 错误元素选择器，默认为'.ant-form-item-has-error'
 * @param options 滚动配置选项
 */
export function scrollToFirstError(
  formContainer: string | HTMLElement = 'form',
  errorSelector: string = '.ant-form-item-has-error',
  options: ScrollOptions = {}
): Promise<void> {
  const container = typeof formContainer === 'string' 
    ? document.querySelector(formContainer) 
    : formContainer;

  if (!container) {
    return Promise.reject(new Error('表单容器未找到'));
  }

  const firstError = container.querySelector(errorSelector);
  if (!firstError) {
    return Promise.reject(new Error('未找到错误元素'));
  }

  return scrollToElement(firstError as HTMLElement, {
    behavior: 'smooth',
    block: 'center',
    offset: -50,
    container: container as HTMLElement,
    ...options
  });
}

/**
 * 滚动到指定位置（绝对坐标）
 * @param x 水平位置
 * @param y 垂直位置
 * @param container 滚动容器
 * @param options 滚动配置选项
 */
export function scrollToPosition(
  x: number,
  y: number,
  container: string | HTMLElement | Window = window,
  options: Omit<ScrollOptions, 'block' | 'inline'> = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const { behavior = 'smooth', duration = 300 } = options;

      // 处理容器参数
      const scrollContainer = typeof container === 'string' 
        ? document.querySelector(container) as HTMLElement
        : container;

      if (!scrollContainer) {
        reject(new Error('滚动容器未找到'));
        return;
      }

      if (behavior === 'smooth' && duration > 0) {
        smoothScrollTo(scrollContainer as Window | HTMLElement, x, y, duration).then(resolve);
      } else {
        if (scrollContainer === window) {
          window.scrollTo({
            left: x,
            top: y,
            behavior: behavior as ScrollBehavior
          });
        } else {
          (scrollContainer as HTMLElement).scrollTo({
            left: x,
            top: y,
            behavior: behavior as ScrollBehavior
          });
        }
        resolve();
      }
    } catch (error) {
      reject(error);
    }
  });
}

// 默认导出主要函数
export default scrollToElement;